import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SerializedError } from '@reduxjs/toolkit';
import {
  fymAPI,
  useDeletePromoChannelConnectionMutation,
  useGetPromoChannelsQuery,
  usePatchPromoChannelMutation,
  useSelectPromoConnectTargetMutation,
  useStartPromoChannelConnectMutation,
} from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { trackEvent } from '../../../analytics/sml/analytics';
import { channelNumbersState } from '../channels/channelNumbersState';
import { platformOrder } from '../platformMeta';
import { emptyChannelNumbers, SerializedSocialChannel, SocialLoginVariant, SocialPlatform } from '../types';
import { useFirstReadPoll } from './useFirstReadPoll';

interface UsePromoteChannelsProps {
  accountId: number;
}

function emptyChannel(platform: SocialPlatform): SerializedSocialChannel {
  return {
    platform,
    handle: null,
    status: 'disconnected',
    bestDayOfWeek: null,
    bestTimeUtc: null,
    autoPublishAvailable: false,
    alternativeLogin: null,
    loginVariant: null,
    connection: null,
    connectionError: null,
    ...emptyChannelNumbers,
  };
}

/**
 * The five channel cards, and the writes that change one.
 *
 * The server only stores a row once a channel has been touched, so the list is
 * filled out to all five platforms here. A card the artist has never opened is
 * `disconnected`, which is the truth, not a missing row.
 */
export function usePromoteChannels({ accountId }: UsePromoteChannelsProps) {
  // While a card shows "Reading your numbers", the list is read again every few seconds.
  const [isReading, setIsReading] = useState(false);
  const pollingInterval = useFirstReadPoll(isReading);
  const { data, isLoading, error, refetch } = useGetPromoChannelsQuery({ accountId }, { pollingInterval });
  const dispatch = useDispatch();
  const wasReading = useRef(false);
  const isReadingNow = useMemo(
    () => data?.channels.some((channel) => channelNumbersState(channel) === 'reading') ?? false,
    [data?.channels]
  );
  useEffect(() => {
    setIsReading(isReadingNow);
    // The first read ended: the growth line and the posts of the channel exist now.
    if (wasReading.current && !isReadingNow) {
      dispatch(fymAPI.util.invalidateTags(['PromoSocialNumbers']));
    }
    wasReading.current = isReadingNow;
  }, [dispatch, isReadingNow]);
  const [patchChannel, { isLoading: isPatching, error: patchError }] = usePatchPromoChannelMutation();
  const [startConnect, { isLoading: isStartingConnect, error: startConnectError }] =
    useStartPromoChannelConnectMutation();
  const [selectConnectTarget, { isLoading: isSelectingTarget, error: selectTargetError }] =
    useSelectPromoConnectTargetMutation();
  const [deleteConnection, { isLoading: isDeletingConnection, error: deleteConnectionError }] =
    useDeletePromoChannelConnectionMutation();
  const [redirectError, setRedirectError] = useState<SerializedError>();

  const channels = useMemo<SerializedSocialChannel[]>(() => {
    const stored = data?.channels;
    return platformOrder.map((platform) => stored?.find((c) => c.platform === platform) ?? emptyChannel(platform));
  }, [data?.channels]);

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  /**
   * Saving a handle is what connects a channel manually, so it also sets `manual`.
   *
   * A connected channel is the exception. The platform owns its handle there,
   * and `manual` would stop auto-posting with the tokens still in place, so the
   * status is left out and the server keeps the row `connected`.
   */
  const saveHandle = useCallback(
    async (platform: SocialPlatform, handle: string): Promise<boolean> => {
      const current = channels.find((channel) => channel.platform === platform);
      const status = current?.status === 'connected' ? undefined : 'manual';
      const result = await patchChannel({ accountId, platform, handle, status });
      return isSuccessfulResult(result);
    },
    [accountId, channels, patchChannel]
  );

  /** Clearing the handle is what disconnects the manual channel. */
  const disconnect = useCallback(
    async (platform: SocialPlatform): Promise<boolean> => {
      const result = await patchChannel({ accountId, platform, handle: null, status: 'disconnected' });
      return isSuccessfulResult(result);
    },
    [accountId, patchChannel]
  );

  /**
   * Hands the browser to the platform's login page.
   *
   * The server needs an address on this origin to send the browser back to, so
   * it gets the Channels screen's own address without its query string — the
   * result is appended to it.
   *
   * `loginVariant` is for Instagram only: `'instagram'` starts the sign-in of Instagram itself
   * and not the default login through a Facebook Page.
   */
  const connect = useCallback(
    async (platform: SocialPlatform, loginVariant?: SocialLoginVariant): Promise<boolean> => {
      setRedirectError(undefined);
      trackEvent('channel_connect_started', { platform });
      try {
        const returnTo = `${window.location.origin}${window.location.pathname}`;
        const result = await startConnect({ accountId, platform, returnTo, loginVariant });
        if (!isSuccessfulResult(result)) {
          return false;
        }
        window.location.assign(result.data.authorizeUrl);
        return true;
      } catch (e) {
        setRedirectError({ message: e instanceof Error ? e.message : String(e) });
        return false;
      }
    },
    [accountId, startConnect]
  );

  /** Picks which page or account of a finished login the posts go to. */
  const selectTarget = useCallback(
    async (platform: SocialPlatform, externalId: string): Promise<boolean> => {
      const result = await selectConnectTarget({ accountId, platform, externalId });
      return isSuccessfulResult(result);
    },
    [accountId, selectConnectTarget]
  );

  /** Drops the tokens. The channel keeps its handle and falls back to manual. */
  const disconnectConnection = useCallback(
    async (platform: SocialPlatform): Promise<boolean> => {
      const result = await deleteConnection({ accountId, platform });
      return isSuccessfulResult(result);
    },
    [accountId, deleteConnection]
  );

  const isWriting = isPatching || isStartingConnect || isSelectingTarget || isDeletingConnection;
  const writeError = patchError ?? startConnectError ?? selectTargetError ?? deleteConnectionError ?? redirectError;

  return {
    channels,
    isLoading,
    error,
    retry,
    saveHandle,
    disconnect,
    connect,
    selectTarget,
    disconnectConnection,
    isWriting,
    writeError,
  };
}
